<script setup>
import { computed, onMounted, nextTick, ref } from 'vue';
import { useSidebarContext } from './provider';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Policy from 'dashboard/components/policy.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarSubGroup from './SidebarSubGroup.vue';
import SidebarGroupEmptyLeaf from './SidebarGroupEmptyLeaf.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
  badge: { type: [Number, String], default: 0 },
  // When true, clicking the group's icon while already viewing one of its
  // sub-pages navigates back to the first child (its "default" view) instead
  // of just toggling the expanded state.
  resetOnActiveClick: { type: Boolean, default: false },
});

const {
  expandedItem,
  setExpandedItem,
  resolvePath,
  resolvePermissions,
  resolveFeatureFlag,
  isAllowed,
} = useSidebarContext();

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const isExpanded = computed(() => expandedItem.value === props.name);
const isExpandable = computed(() => props.children);
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  return props.children.filter(child => {
    // If a item has no link, it means it's just a subgroup header
    // So we don't need to check for permissions here, because there's nothing to
    // access here anyway
    return child.to && isAllowed(child.to);
  });
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0;
});

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) {
      const expectedQuery = props.to.query || {};
      return Object.entries(expectedQuery).every(
        ([key, value]) => String(route.query[key] ?? '') === String(value)
      );
    }

    return props.activeOn.includes(route.name);
  }

  return false;
});

// We could use the RouterLink isActive too, but our routes are not always
// nested correctly, so we need to check the active state ourselves
// TODO: Audit the routes and fix the nesting and remove this
const activeChild = computed(() => {
  const pathSame = navigableChildren.value.find(
    child =>
      child.to &&
      route.path === resolvePath(child.to) &&
      Object.entries(child.to.query || {}).every(
        ([key, value]) => String(route.query[key] ?? '') === String(value)
      )
  );
  if (pathSame) return pathSame;

  // Rank the activeOn Prop higher than the path match
  // There will be cases where the path name is the same but the params are different
  // So we need to rank them based on the params
  // For example, contacts segment list in the sidebar effectively has the same name
  // But the params are different
  const activeOnPages = navigableChildren.value.filter(child =>
    child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    const rankedPage = activeOnPages.find(child => {
      return Object.keys(child.to.params)
        .map(key => {
          return String(child.to.params[key]) === String(route.params[key]);
        })
        .every(match => match);
    });

    // If there is no ranked page, return the first activeOn page anyway
    // Since this takes higher precedence over the path match
    // This is not perfect, ideally we should rank each route based on all the techniques
    // and then return the highest ranked one
    // But this is good enough for now
    return rankedPage ?? activeOnPages[0];
  }

  return navigableChildren.value.find(
    child => child.to && route.path.startsWith(resolvePath(child.to))
  );
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const toggleTrigger = () => {
  if (hasAccessibleChildren.value) {
    if (!isExpanded.value && !hasActiveChild.value) {
      // if not already expanded, navigate to the first child
      const firstItem = accessibleItems.value[0];
      router.push(firstItem.to);
    } else if (props.resetOnActiveClick && hasActiveChild.value) {
      // already viewing a sub-page under this group: clicking the icon
      // again takes you back to the group's default (first) view
      const firstItem = accessibleItems.value[0];
      router.push(firstItem.to);
    }
  }
  setExpandedItem(props.name);
};

onMounted(async () => {
  await nextTick();
  if (hasActiveChild.value) {
    setExpandedItem(props.name);
  }
});

// The desktop, icon-only sidebar reveals children via a flyout on hover/focus.
// It's teleported to <body> (rather than absolutely positioned inside the <li>)
// because the sidebar's <nav> scrolls vertically, and a scrolling ancestor
// clips any horizontal overflow too (a CSS overflow-x/y quirk), which was
// silently hiding the flyout.
const isRTL = useMapGetter('accounts/isRTL');
const triggerRef = ref(null);
const isFlyoutOpen = ref(false);
const flyoutPosition = ref({});
let closeTimer = null;

const FLYOUT_MARGIN = 16;
const FLYOUT_MIN_HEIGHT = 200;

const positionFlyout = () => {
  const el = triggerRef.value?.$el;
  if (!el) return;

  const rect = el.getBoundingClientRect();

  // Long menus (e.g. Settings) can open near the bottom of the screen, where there
  // isn't enough room below the icon to fit them. Flip to grow upward from the
  // icon instead, and clamp the height to whichever space we actually have so the
  // list scrolls internally rather than running off the edge of the screen.
  const spaceBelow = window.innerHeight - rect.top - FLYOUT_MARGIN;
  const spaceAbove = rect.bottom - FLYOUT_MARGIN;
  const openUpward = spaceBelow < FLYOUT_MIN_HEIGHT && spaceAbove > spaceBelow;

  const vertical = openUpward
    ? {
        bottom: `${window.innerHeight - rect.bottom}px`,
        maxHeight: `${spaceAbove}px`,
      }
    : { top: `${rect.top}px`, maxHeight: `${spaceBelow}px` };

  const horizontal = isRTL.value
    ? { right: `${window.innerWidth - rect.left + 8}px` }
    : { left: `${rect.right + 8}px` };

  flyoutPosition.value = { ...vertical, ...horizontal };
};

const openFlyout = () => {
  if (!hasChildren.value) return;

  clearTimeout(closeTimer);
  positionFlyout();
  isFlyoutOpen.value = true;
};

const scheduleCloseFlyout = () => {
  clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    isFlyoutOpen.value = false;
  }, 150);
};

const cancelCloseFlyout = () => {
  clearTimeout(closeTimer);
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    ref="triggerRef"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="grid cursor-pointer select-none gap-1 overflow-visible text-sm md:w-12"
    @mouseenter="openFlyout"
    @mouseleave="scheduleCloseFlyout"
    @focusin="openFlyout"
    @focusout="scheduleCloseFlyout"
  >
    <SidebarGroupHeader
      :icon
      :name
      :label
      :to
      :getter-keys="getterKeys"
      :badge="badge"
      :is-active="isActive"
      :has-active-child="hasActiveChild"
      :expandable="hasChildren"
      :is-expanded="isExpanded"
      @toggle="toggleTrigger"
    />
    <ul
      v-if="hasChildren"
      v-show="isExpanded || hasActiveChild"
      class="grid m-0 list-none sidebar-group-children md:hidden"
    >
      <template v-for="child in children" :key="child.name">
        <SidebarSubGroup
          v-if="child.children"
          :label="child.label"
          :icon="child.icon"
          :children="child.children"
          :is-expanded="isExpanded"
          :active-child="activeChild"
        />
        <SidebarGroupLeaf
          v-else-if="isAllowed(child.to)"
          v-show="isExpanded || activeChild?.name === child.name"
          v-bind="child"
          :active="activeChild?.name === child.name"
        />
      </template>
    </ul>
    <ul v-else-if="isExpandable && isExpanded">
      <SidebarGroupEmptyLeaf />
    </ul>
  </Policy>
  <TeleportWithDirection v-if="hasChildren" to="body">
    <ul
      v-show="isFlyoutOpen"
      class="fixed z-50 m-0 hidden min-w-64 list-none overflow-y-auto rounded-3xl border border-white/70 bg-white/95 p-3 shadow-2xl shadow-slate-900/10 backdrop-blur-xl dark:border-n-weak dark:bg-n-solid-2/95 md:block"
      :style="flyoutPosition"
      @mouseenter="cancelCloseFlyout"
      @mouseleave="scheduleCloseFlyout"
    >
      <li
        class="px-3 pb-3 text-xs font-semibold uppercase tracking-[0.16em] text-n-slate-9"
      >
        {{ label }}
      </li>
      <template v-for="child in children" :key="`flyout-${child.name}`">
        <SidebarSubGroup
          v-if="child.children"
          :label="child.label"
          :icon="child.icon"
          :children="child.children"
          is-expanded
          :active-child="activeChild"
        />
        <SidebarGroupLeaf
          v-else-if="isAllowed(child.to)"
          v-bind="child"
          :active="activeChild?.name === child.name"
        />
      </template>
    </ul>
  </TeleportWithDirection>
</template>

<style>
.sidebar-group-children .child-item::before {
  content: '';
  position: absolute;
  width: 0.125rem;
  /* 0.5px */
  height: 100%;
}

.sidebar-group-children .child-item:first-child::before {
  border-radius: 4px 4px 0 0;
}

/* This selects the last child in a group */
/* https://codepen.io/scmmishra/pen/yLmKNLW */
.sidebar-group-children > .child-item:last-child::before,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::before {
  height: 20%;
}

.sidebar-group-children > .child-item:last-child::after,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 12px;
  bottom: calc(50% - 2px);
  border-bottom-width: 0.125rem;
  border-left-width: 0.125rem;
  border-right-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 0 4px;
  left: 0;
}

#app[dir='rtl'] .sidebar-group-children > .child-item:last-child::after,
#app[dir='rtl']
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  right: 0;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  border-left-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 4px 0px;
}
</style>
